import { useEffect, useState, useMemo } from "react";
import tinycolor from "tinycolor2";

import { scrollToTop } from "../utils";
import { compileTemplate } from "../utils/formatting";
import { getAppointmentTheme } from "../utils/theme";

import { openGoogleCalendar, downloadIcsFile } from "../utils/calendar";
import WeeConfetti from "../css/WeeConfetti";

const AppointmentConfirmed = ({ appointment }) => {
  const [confirmationMessage, setConfirmationMessage] = useState(
    "We'll see you soon!"
  );

  const { accentColor, gradientBackground } = useMemo(
    () => getAppointmentTheme(appointment),
    [appointment]
  );

  useEffect(() => {
    scrollToTop();
  });

  useEffect(() => {
    buildHandlebars();
  }, []);

  const buildHandlebars = () => {
    const result = compileTemplate(
      appointment?.shopData?.messages?.confirmationMessage ??
        "Your appointment has been successfully booked! We look forward to seeing you soon.",
      {
        your_service: appointment?.service,
        on_date: appointment?.date,
        at_time: formatTimeForCustomer(appointment?.time),
      }
    );
    setConfirmationMessage(result);
  };

  const confirmLeavePage = () => {
    const outlet = appointment?.shopData?.shopContact?.socialLinks?.[0]?.url;
    if (outlet) {
      window.location.replace(outlet);
    }
  };

  const formatTimeForCustomer = (timeString) => {
    // If it contains a dash (range format), extract only the start time
    if (timeString?.includes(" - ")) {
      return timeString.split(" - ")[0];
    }
    return timeString;
  };

  return (
    <div
      className="flex flex-col items-center min-h-full font-roboto overflow-x-hidden"
      style={{ background: gradientBackground }}
    >
      <div className="w-full max-w-md px-4 py-8 md:px-6 box-border">
        {/* Confirmation Card */}
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
        >
          <div className="flex flex-col items-center px-6 pt-6 pb-4">
            {/* Checkmark icon */}
            <div
              className="flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ backgroundColor: tinycolor(accentColor).lighten(35).toHexString() }}
            >
              <i
                className="fa-solid fa-circle-check text-3xl"
                style={{ color: accentColor, lineHeight: 1 }}
                aria-hidden="true"
              />
            </div>

            <div className="text-2xl font-bold text-gray-900 mb-2 text-center">
              Appointment Confirmed!
            </div>

            <div className="text-center text-gray-600 text-sm leading-relaxed mb-4">
              <div
                style={{
                  fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                  whiteSpace: "pre-wrap",
                  overflowWrap: "break-word",
                }}
              >
                {confirmationMessage}
              </div>
            </div>

          </div>

          {/* Add to Calendar */}
          <div className="px-6 pb-2">
            <div
              className="rounded-xl border-2 border-solid p-4"
              style={{ borderColor: `${accentColor}44` }}
            >
              <div
                className="text-xs font-semibold uppercase tracking-wide mb-3 text-center"
                style={{ color: accentColor }}
              >
                Add to Calendar
              </div>
              <div className="flex flex-row gap-3">
                <button
                  type="button"
                  aria-label="Add to Apple Calendar"
                  className="flex-1 flex items-center justify-center min-h-[44px] rounded-lg border-2 border-solid cursor-pointer transition-opacity hover:opacity-80 active:opacity-70 focus:outline-none"
                  style={{
                    backgroundColor: "#ffffff",
                    borderColor: accentColor,
                    color: accentColor,
                    boxShadow: "none",
                  }}
                  onClick={() => downloadIcsFile(appointment)}
                >
                  <i className="fa-brands fa-apple text-lg" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Add to Google Calendar"
                  className="flex-1 flex items-center justify-center min-h-[44px] rounded-lg border-2 border-solid cursor-pointer transition-opacity hover:opacity-80 active:opacity-70 focus:outline-none"
                  style={{
                    backgroundColor: "#ffffff",
                    borderColor: accentColor,
                    color: accentColor,
                    boxShadow: "none",
                  }}
                  onClick={() => openGoogleCalendar(appointment)}
                >
                  <i className="fa-brands fa-google text-lg" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>

          {/* Close button */}
          <div className="flex justify-center px-6 py-5">
            <button
              type="button"
              className="flex items-center gap-2 rounded-lg px-8 py-2.5 text-sm font-semibold text-white cursor-pointer transition-opacity hover:opacity-90 active:opacity-80 focus:outline-none"
              style={{
                backgroundColor: accentColor,
                border: "none",
                boxShadow: "none",
              }}
              onClick={confirmLeavePage}
            >
              Close
            </button>
          </div>
        </div>
      </div>
      <WeeConfetti />
    </div>
  );
};

export { AppointmentConfirmed };
